import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useReducer } from 'react';
import { useSelector } from 'react-redux';
import CustomTable from 'shared/components/CustomTable';
import ListResModal from 'shared/components/DeleteModal';
import { Button } from 'shared/io';
import { useRefresh } from 'shared/utils/hooks';
import { reducerState } from 'shared/utils/view';
import { transactionActions } from 'store/transaction/transaction.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { StoreState } from 'types/storeTypes';

import HistoryListItem from './HistoryListItem';
import styleIdentifiers from './history.module.scss';
import PaymentsFiltersForm from './PaymentsFiltersForm';

const styles = classNames.bind(styleIdentifiers);
const EnhancedTable = CustomTable(HistoryListItem, PaymentsFiltersForm);

type State = {
  selectAll: boolean;
  selectedItems: any[];
};

export default function History() {
  const { t } = useTranslation();
  const paymentHistory = useSelector((state: StoreState) => state.transaction.paymentHistory!);
  const [{ selectedItems, selectAll: isAllSelected }, setState] = useReducer<State, any>(
    reducerState,
    {
      selectAll: false,
      selectedItems: [],
    },
  );
  const { refresh, doRefresh } = useRefresh();

  const TABLE_HEADERS = [
    {
      key: 'transaction_id',
      title: t(i18nKeys.BANK_ACCOUNT.HISTORY.TRANSACTION_NUMBER),
      width: 70,
    },
    {
      title: t(i18nKeys.COMMON.BANK_ACCOUNT),
      width: 140,
    },
    {
      key: 'counterparty_name',
      title: t(i18nKeys.BANK_ACCOUNT.HISTORY.SENDER),
    },
    {
      key: 'value_date',
      title: t(i18nKeys.DATE),
    },
    {
      key: 'remaining_balance',
      title: t(i18nKeys.AMOUNT),
    },
    {
      title: t(i18nKeys.COMMUNICATION),
      width: 400,
    },
    {
      title: '',
      width: 60,
    },
  ];

  const handleFilters = (filters: any) => {
    const newFilters = {
      ...filters,
      bank_account_ids: filters.bank_account_ids?.length > 0 ? filters.bank_account_ids : undefined,
    };

    // need to be linked
    if ((newFilters.operator && !newFilters.amount) || (!newFilters.operator && newFilters.amount))
      return false;

    return newFilters;
  };

  const handleQueryParams = (queryParams: any) => {
    queryParams.bank_account_ids = queryParams.bank_account_ids
      ? queryParams.bank_account_ids.split(',')
      : undefined;
    return queryParams;
  };

  const batchActions = (action: string) => () => {
    let data = {} as any;
    if (isAllSelected) data = handleQueryParams(qs.parse(location.search));

    transactionActions[action as keyof typeof transactionActions]({
      data: {
        ...data,
        feedback: true,
        payment_ids: isAllSelected ? [] : selectedItems.map(payment => payment.id),
      },
      callback: (res: any) => {
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.MEDIUM,
          title: t(i18nKeys.RESULT),
          children: <ListResModal data={res.data} />,
        });

        transactionActions.paymentHistoryPage(
          handleQueryParams(qs.parse(location.search.slice(1))),
        );

        setState({
          selectAll: false,
          selectedItems: [],
        });

        doRefresh();
      },
    });
  };

  const onCheckBoxChange = (_selectedItems: any[]) => {
    setState({ selectedItems: _selectedItems });
  };

  const onSelectAll = (value: boolean) => {
    setState({
      selectAll: value,
    });
  };

  return (
    <div className={styles('Listing')}>
      <EnhancedTable
        title={t(i18nKeys.NAV.PAYMENT_BOOK)}
        loadFunc={transactionActions.paymentHistoryPage}
        loading={paymentHistory.loading}
        orderField={(field: any) => field}
        handleLoadData={handleQueryParams}
        loaded={paymentHistory.loaded}
        pagination={paymentHistory.metadata?.pagination}
        headers={TABLE_HEADERS}
        handleFilters={handleFilters}
        items={paymentHistory.pages}
        itemProps={{ type: 'journal' }}
        setSelectAll={onSelectAll}
        resetSelection={refresh}
        onCheckBoxChange={onCheckBoxChange}
        actions={
          (selectedItems.length > 0 || isAllSelected) && (
            <div className={styles('actions')}>
              <Button
                noMargin
                label={t(i18nKeys.BANK_ACCOUNT.HISTORY.UNIGNORED_PAYMENT)}
                onClick={batchActions('unignorePayments')}
              />
              <Button
                noMargin
                label={t(i18nKeys.BANK_ACCOUNT.RECONCILIATION.IGNORE_PAYMENTS)}
                onClick={batchActions('ignorePayments')}
              />
            </div>
          )
        }
      />
    </div>
  );
}
